import {
  Box,
  Button,
  Flex,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import type { MergedPricingContract } from 'screens/Prices/utils/mergePricingService';
import { trackSelectItem } from 'utils/tracking';
import { getBrandColor } from 'utils/brandColor';

const cardBorderRadius = `${Number(mondayMindbody?.customization?.cardBorderRadius) || 12}px`;
const brandColor = getBrandColor();

interface ContractCardProps {
  merged: MergedPricingContract;
}

/**
 * Visual card for a single Mindbody contract — same layout as PackCard
 * (maquette style).
 */
export function ContractCard({ merged }: ContractCardProps) {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const onClick = () => {
    trackSelectItem({ ...merged.contract, type: 'contract' } as unknown as Record<string, unknown>);
    navigate(`/checkout/contract/${merged.contract.Id}`, {
      state: { contract: merged.contract, merged },
    });
  };

  const accentColor = merged.ribbon?.color || brandColor;
  const hasBottomInfo = !!(merged.validity || merged.limitText);
  const hasBottomRow = !!(hasBottomInfo || merged.ctaLabel);

  const engagementCount = merged.contract.NumberOfAutopays ?? null;

  return (
    <Box
      className="serviceCard serviceCard--contract"
      borderRadius={cardBorderRadius}
      overflow="hidden"
      bg="#f5f5f5"
      display="flex"
      flexDirection="column"
      minH="280px"
    >
      {/* ── Ribbon bar — reserve space even when absent ── */}
      <Box
        className={merged.ribbon ? 'serviceRibbon' : 'serviceRibbonSpacer'}
        w="full"
        px={5}
        py={merged.ribbon ? 2.5 : 0}
        minH="40px"
        bg={merged.ribbon ? merged.ribbon.color || accentColor : 'transparent'}
        color="white"
        fontSize="sm"
        fontWeight="700"
        textTransform="uppercase"
        letterSpacing="wide"
        flexShrink={0}
        display="flex"
        alignItems="center"
      >
        {merged.ribbon?.text}
      </Box>

      {/* ── Image ── */}
      {merged.image && merged.image.url && (
        <Image
          className="serviceCardImage"
          src={merged.image.url}
          alt=""
          w="full"
          h="140px"
          objectFit="cover"
          flexShrink={0}
        />
      )}

      {/* ── Text content ── */}
      <Flex className="serviceCardBody" direction="column" flex="1" px={5} pt={4} pb={4}>
        {merged.title && (
          <Text
            className="serviceName"
            fontSize={{ base: '18px', md: '20px' }}
            fontWeight="900"
            textTransform="uppercase"
            lineHeight="1.1"
            color="#1a1a1a"
          >
            {merged.title}
          </Text>
        )}

        {merged.priceLabel && !merged.hidePrice && (
          <Box className="servicePriceBlock" mt={3}>
            <Text
              className="servicePrice"
              fontSize={{ base: '22px', md: '26px' }}
              fontWeight="400"
              textTransform="uppercase"
              color="#1a1a1a"
              lineHeight="1"
            >
              <Text as="span" fontWeight="900">{merged.priceAmount}</Text>{merged.priceSuffix}
            </Text>
            {merged.pricePerSessionLabel && (
              <Text
                className="servicePricePerSession"
                fontSize="13px"
                fontWeight="400"
                textTransform="uppercase"
                color="#1a1a1a"
                mt={1}
              >
                {merged.pricePerSessionLabel}
              </Text>
            )}
          </Box>
        )}

        <Box flex="1" minH={3} />

        {/* ── Bottom row ── */}
        {hasBottomRow && (
          <Flex className="serviceCardBottomRow" align="flex-end" justify="space-between" gap={3}>
            {merged.ctaLabel ? (
              <Button
                className="serviceCardButton"
                borderRadius="full"
                onClick={onClick}
                bg={accentColor}
                color="white"
                _hover={{ opacity: 0.9 }}
                px={6}
                h="38px"
                fontSize="13px"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="wide"
                flexShrink={0}
              >
                {merged.ctaLabel}
              </Button>
            ) : (
              <Box />
            )}

            {hasBottomInfo && (
              <Box className="serviceCardBottomInfo" textAlign="right">
                {merged.validity && (
                  <Text
                    className="serviceEngagement"
                    fontSize="12px"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="wide"
                    color="#1a1a1a"
                    lineHeight="1.4"
                  >
                    {merged.validity}
                  </Text>
                )}
                {merged.limitText && (
                  <Text className="serviceLimitText" fontSize="11px" color="#4a4a4a" lineHeight="1.4">
                    {merged.limitText}
                  </Text>
                )}
              </Box>
            )}
          </Flex>
        )}
      </Flex>

      {/* ── Bottom ribbon "En savoir plus" — opens the details modal ── */}
      <Flex
        as="button"
        type="button"
        className="serviceRibbon serviceRibbon--bottom"
        onClick={onOpen}
        w="full"
        px={5}
        py={2.5}
        minH="40px"
        bg="transparent"
        color={accentColor}
        fontSize="sm"
        fontWeight="700"
        textTransform="uppercase"
        letterSpacing="wide"
        textDecoration="underline"
        align="center"
        justify="center"
        gap={2}
        flexShrink={0}
        cursor="pointer"
        _hover={{ opacity: 0.8 }}
      >
        En savoir plus
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent className="contractDetailsModal" sx={{ '&, & *': { textTransform: 'none' } }}>
          <ModalHeader pr={12}>{merged.title || "Détails de l'abonnement"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {merged.contractTerms ? (
              <Box
                className="contractDetailsModalCustomTerms"
                fontSize="14px"
                color="#4a4a4a"
                lineHeight="1.5"
                textAlign="left"
                sx={{
                  '& p': { mb: 3 },
                  '& p:last-child': { mb: 0 },
                  '& strong, & b': { color: '#1a1a1a', fontWeight: 700 },
                  '& a': { textDecoration: 'underline' },
                  '& ul, & ol': { pl: 5, mb: 3 },
                }}
                dangerouslySetInnerHTML={{ __html: merged.contractTerms }}
              />
            ) : (
              <Stack spacing={3} fontSize="14px" color="#4a4a4a" lineHeight="1.5" textAlign="left">
                <Text textTransform="none">
                  <Text as="span" fontWeight="700" color="#1a1a1a">
                    🔁 Report des sessions :
                  </Text>{' '}
                  les sessions incluses dans votre abonnement sont valables uniquement sur le mois en cours et ne sont pas
                  reportables sur le mois suivant.
                </Text>
                <Text textTransform="none">
                  <Text as="span" fontWeight="700" color="#1a1a1a">
                    📅 Durée d'engagement :
                  </Text>{' '}
                  {engagementCount
                    ? `durée d'engagement minimum de ${engagementCount} mois à compter de la date de souscription.`
                    : "durée d'engagement minimum à compter de la date de souscription."}
                </Text>
                <Text textTransform="none">
                  <Text as="span" fontWeight="700" color="#1a1a1a">
                    🔄 Reconduction tacite :
                  </Text>{' '}
                  à l'issue de la période d'engagement, l'abonnement est reconduit tacitement pour une durée illimitée par
                  périodes successives d'un mois entier.
                </Text>
                <Text textTransform="none">
                  <Text as="span" fontWeight="700" color="#1a1a1a">
                    🚀 Date de début d'abonnement :
                  </Text>{' '}
                  votre abonnement débute le jour de la souscription, avec un premier paiement effectué le jour même.
                </Text>
                <Text textTransform="none">
                  <Text as="span" fontWeight="700" color="#1a1a1a">
                    🎟️ Validité des crédits :
                  </Text>{' '}
                  profitez de vos crédits dans l'ensemble des studios Dynamo, RIISE et Punch, y compris Reformer et
                  Lagree.
                </Text>
                {merged.contractPdfUrl && (
                  <Text textTransform="none">
                    <Text as="span" fontWeight="700" color="#1a1a1a">
                      📜 CGV complémentaires :
                    </Text>{' '}
                    pour plus de détails, consultez nos{' '}
                    <Link
                      href={merged.contractPdfUrl}
                      isExternal
                      textDecoration="underline"
                      color="inherit"
                      _hover={{ opacity: 0.8 }}
                    >
                      Conditions Générales de Vente
                    </Link>
                    .
                  </Text>
                )}
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
